import { Component, OnInit, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';

import { Subscription, zip } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import {
  IncidentsQualityAnalysisService,
  IIncidentQualityAnalysis,
  IncidentQualityAnalysisQuestion,
  IncidentQualityAnalysisTemplate,
  IncidentsQualityAnalysisTemplateService,
} from '@libs/portal-common/services';
import { IEditableComponent, AppNotificationsService } from '@libs/portal-common/system';
import { PdfExporterService } from '@libs/portal-common/shared';
import moment from 'moment';
import Papa from 'papaparse';

@Component({
  selector: 'app-incident-quality-analysis',
  templateUrl: './incident-quality-analysis.component.html',
  styleUrls: ['./incident-quality-analysis.component.scss'],
})
export class IncidentQualityAnalysisComponent implements OnInit, OnDestroy, IEditableComponent {
  @ViewChild('form', { static: false }) public form: NgForm;
  @ViewChild('formContent', { static: false, read: ElementRef }) public formContent: ElementRef;

  editing = false;

  subscription: Subscription;

  validate = false;
  model: IIncidentQualityAnalysis = {
    Id: 0,
    fkIncident: null,
    Created: null,
    fkLot: null,
    fkIncidentQualityAnalysisTemplate: null,
    IncidentQualityAnalysisAnswers: [],
  };
  dynamicModel: any = {};
  modelCopy: string;
  submitPressed = false;

  questions: Array<IncidentQualityAnalysisQuestion> = [];
  templates: Array<IncidentQualityAnalysisTemplate> = [];
  selectedTemplate: IncidentQualityAnalysisTemplate = null;

  errorText = '';
  modelNotExist = false;

  constructor(
    private route: ActivatedRoute,
    private IncidentsQualityAnalysisTemplate: IncidentsQualityAnalysisTemplateService,
    private incidentsQualityAnalysis: IncidentsQualityAnalysisService,
    private notifications: AppNotificationsService,
    private pdfExporter: PdfExporterService,
  ) {}

  ngOnInit() {
    this.subscription = zip(this.route.parent.params, this.route.queryParams)
      .pipe(
        mergeMap(([params, queryParams]) => {
          const incidentId = +params['id'];
          const templateId = queryParams['templateId'] ? +queryParams['templateId'] : null;
          return this.IncidentsQualityAnalysisTemplate.getAll(true).pipe(
            map((templatesResp) => ({ incidentId, templateId, templatesResp })),
          );
        }),
      )
      .subscribe(
        ({ incidentId, templateId, templatesResp }) => {
          this.model.fkIncident = incidentId;
          this.templates = templatesResp.Data || [];
          if (templateId && this.templates?.length > 0) {
            this.selectedTemplate = this.templates.find((t) => t.Id === templateId);
          } else if (this.templates?.length > 0) {
            this.selectedTemplate = this.templates[0];
          }
          if (this.selectedTemplate) {
            this.loadAnalysisForTemplate(this.model.fkIncident, this.selectedTemplate.Id);
          }
        },
        (err) => {
          if (err?.error?.AppErrorCode === 'ManualHandleNotFound' && err?.error?.Message) {
            this.modelNotExist = true;
            this.errorText = err?.error?.Message || 'Incident not found.';
          }
        },
      );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  hasChanges(): boolean {
    return JSON.stringify(this.dynamicModel) !== this.modelCopy;
  }

  submit() {
    this.submitPressed = true;
    this.validate = true;

    if (!this.form.valid) {
      return;
    }

    this.model.IncidentQualityAnalysisAnswers = [];
    let questionsWithOutSoftDelete: Array<IncidentQualityAnalysisQuestion> = this.questions.filter((x) => !x.IsDeleted);
    questionsWithOutSoftDelete.forEach((question) => {
      if (question.AnswerType.toString() === 'MultiChoice') {
        this.model.IncidentQualityAnalysisAnswers.push({
          fkQualityAnalysisQuestion: question.Id,
          Answer: JSON.stringify(this.dynamicModel[question.Id]),
        });
      } else {
        this.model.IncidentQualityAnalysisAnswers.push({
          fkQualityAnalysisQuestion: question.Id,
          Answer: this.dynamicModel[question.Id],
        });
      }
    });

    this.incidentsQualityAnalysis.createOrUpdate(this.model).subscribe((response) => {
      if (this.model.Id === 0) {
        this.model.Id = response.QualityAnalysis.Id;
      }
      if (this.model.fkLot === null) {
        this.model.fkLot = response.QualityAnalysis.fkLot;
      }

      if (this.model.Created === null) {
        this.model.Created = response.QualityAnalysis.Created;
      }

      this.setDynamicModel(response.QualityAnalysis);
      this.fillSystemFields();
      this.modelCopy = JSON.stringify(this.dynamicModel);
      this.notifications.successUpdated();
      this.editing = false;
    });
  }

  showEdit() {
    this.editing = true;
    this.submitPressed = false;
  }

  cancelEdit() {
    this.editing = false;
    this.submitPressed = false;
    if (this.hasChanges() && this.modelCopy) {
      this.cancel();
    }
  }

  private cancel() {
    this.dynamicModel = JSON.parse(this.modelCopy);
  }

  private setModel(model: IIncidentQualityAnalysis) {
    this.model = model;
  }

  private setDynamicModel(model: IIncidentQualityAnalysis) {
    model.IncidentQualityAnalysisAnswers.forEach(({ fkQualityAnalysisQuestion, Answer }) => {
      const question = this.questions.find((x) => x.Id === fkQualityAnalysisQuestion);
      if (question) {
        if (question.AnswerType.toString() === 'MultiChoice') {
          this.dynamicModel[fkQualityAnalysisQuestion] = JSON.parse(Answer);
        } else {
          this.dynamicModel[fkQualityAnalysisQuestion] = Answer;
        }
      }
    });
  }

  onTemplateChange(template: IncidentQualityAnalysisTemplate): void {
    if (!template) {
      return;
    }

    this.selectedTemplate = template;

    this.loadAnalysisForTemplate(this.model.fkIncident, template.Id);
  }

  private loadAnalysisForTemplate(incidentId: number, templateId: number) {
    this.questions = [];
    this.modelNotExist = false;
    this.errorText = '';

    this.incidentsQualityAnalysis.get({ fkIncident: incidentId, templateId: templateId }).subscribe(
      (answers) => {
        this.model = {
          Id: 0,
          Created: null,
          fkIncident: incidentId,
          fkIncidentQualityAnalysisTemplate: templateId,
          IncidentQualityAnalysisAnswers: [],
          fkLot: null,
        };

        const selectedTemplate = this.templates.find((t) => t.Id === templateId);

        this.editing = false;
        this.validate = false;
        this.dynamicModel = {};

        if (answers.QualityAnalysis) {
          // If analysis exists, show only answered questions. New questions from the template will not appear.
          const answeredQuestionIds = new Set(
            answers.QualityAnalysis.IncidentQualityAnalysisAnswers.map((a) => a.fkQualityAnalysisQuestion),
          );
          // A soft-deleted question that has an answer should still be displayed as read-only.
          this.questions = (selectedTemplate?.Questions || []).filter((q) => answeredQuestionIds.has(q.Id));

          this.setDynamicModel(answers.QualityAnalysis);
          this.setModel({
            ...this.model,
            Id: answers.QualityAnalysis.Id,
            fkLot: answers.QualityAnalysis.fkLot,
            Created: answers.QualityAnalysis.Created,
          });

          this.fillSystemFields();
          this.modelCopy = JSON.stringify(this.dynamicModel);
        } else {
          // If no analysis, show all visible, non-deleted questions from the selected template
          this.questions = (selectedTemplate?.Questions || []).filter((q) => q.Visible && !q.IsDeleted);

          this.fillSystemFields();
          this.modelCopy = JSON.stringify(this.dynamicModel);
        }
      },
      (err) => {
        if (err?.error?.AppErrorCode === 'ManualHandleNotFound' && err?.error?.Message) {
          this.modelNotExist = true;
          this.errorText = err?.error?.Message || 'Incident not found.';
        }
      },
    );
  }

  private itemSelected(answer, questionId) {
    if (!this.dynamicModel[questionId]) {
      this.dynamicModel[questionId] = [];
    }

    if (!this.dynamicModel[questionId].includes(answer)) {
      this.dynamicModel[questionId] = [...this.dynamicModel[questionId], answer];
    } else {
      const index = this.dynamicModel[questionId].indexOf(answer);
      this.dynamicModel[questionId].splice(index, 1);
    }
  }

  private fillSystemFields() {
    this.questions.forEach((question) => {
      if (question.IsSystem) {
        if (question.QuestionName === 'System Incident ID') {
          this.dynamicModel[question.Id] = this.model.fkIncident;
        } else if (question.QuestionName === 'System Survey Create Date') {
          const createdDate = this.model.Created ? moment.utc(this.model.Created) : moment();
          this.dynamicModel[question.Id] = createdDate.format('YYYY-MM-DD HH:mm:ss');
        }
      }
    });
  }

  exportToPDF(): void {
    if (!this.formContent) {
      return;
    }

    const fileName = `Quality Analysis - Incident ${this.model.fkIncident}.pdf`;
    this.pdfExporter.pdfExport(this.formContent, fileName, 'A4');
    this.notifications.success('Your export is ready! The file will download automatically.');
  }

  exportToCSV(): void {
    if (!this.questions || this.questions.length === 0) {
      return;
    }

    const csvData = this.questions.map((question) => {
      let answer: string;

      if (question.AnswerType.toString() === 'MultiChoice') {
        const answers = this.dynamicModel[question.Id] || [];
        answer = Array.isArray(answers) ? answers.join('; ') : answers;
      } else {
        answer = this.dynamicModel[question.Id] || '';
      }

      return {
        Question: question.Question,
        Answer: answer,
      };
    });

    const fileName = `Quality Analysis - Incident ${this.model.fkIncident}.csv`;
    const csv = Papa.unparse(csvData, {
      quotes: true,
      delimiter: ',',
      header: true,
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    try {
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
    } finally {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      URL.revokeObjectURL(url);
    }
  }
}
